import React, { useState, useEffect, useRef, useCallback } from "react";
import { useForm } from "react-hook-form";
import TableWrapper from "../../core/BaseComponents/TableWrapper/TableWrapper";
import dayjs from "dayjs";
import { Tooltip, CircularProgress } from "@mui/material";
import { MdModeEditOutline } from "react-icons/md";
import { callAPI } from "../../../axios";
import DeleteIcon from "@mui/icons-material/Delete";
import BasicModal from "../../core/BaseComponents/Modal";
import { FaHotel } from "react-icons/fa";
import { ScrollToTop, getUserRoleCodeFromStorage, isValidRole } from "../../../utils/utils";
import { faLink } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DeleteThirdPartyConfig from "./DeleteThirdPartyConfig";
import Custom_Paingation from "../../core/BaseComponents/Custom_Paingation";
import "./ThirdPartyHotelConfiguration.scss";

function ThirdPartyHotelConfiguration() {
  const [thirdPartyConfigList, setThirdPartyConfigList] = useState([])
  const [confirmDeleteThirdPartyRow, setConfirmDeleteThirdPartyRow] = useState("");
  const [confirmValidate, setConfirmValidate] = useState(false);
  const [deleteThirdPartyPopUp, setDeleteThirdPartyPopUp] = useState(false);
  const [createThirdPartySuccessMsg, setCreateThirdPartySuccessMsg] = useState('');
  const [isViewMode, setIsViewMode] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editBrandId, setEditBrandId] = useState(null);
  const [otpSent, setOtpSent] = useState(false);
  const [otpSuccessMessage, setOtpSuccessMessage] = useState('');
  const [otpSendLoading, setOtpSendLoading] = useState(false);
  const [resendOtpSecondsLeft, setResendOtpSecondsLeft] = useState(0);
  const [loader, setLoader] = useState(false);

  const userRoleCode = getUserRoleCodeFromStorage();
  const isSuperAdmin = userRoleCode === "ROLE_SUPER_ADMIN";
  const originalBrandRef = useRef({ brandName: '', displayName: '' });
  const resendOtpTimerRef = useRef(null);

  const [selectedPage, setSelectedPage] = useState(1);
  const [totalPageCount, setTotalPageCount] = useState(1);
  const [paginationError, setPaginationError] = useState("")
  const [inputPageValue, setInputPageValue] = useState(1)

  const recordsPerPage = 20;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setError,
    clearErrors,
  } = useForm({
    mode: 'onChange',
    defaultValues: { active: true, brandName: '', displayName: '', otp: '' }
  });

  const brandNameWatched = watch('brandName');
  const isBrandNameChangedFromOriginal = (brandName) =>
    isEditMode &&
    String(brandName ?? '').trim().toLowerCase() !==
      String(originalBrandRef.current.brandName ?? '').trim().toLowerCase();

  const brandNameChanged = isBrandNameChangedFromOriginal(brandNameWatched);

  const { onChange: otpFormOnChange, ...otpFieldRegister } = register("otp", {
    setValueAs: (v) => String(v ?? "").replace(/\D/g, "").slice(0, 6),
    validate: (value) => {
      if (!brandNameChanged) return true;
      if (!value) return "OTP is required";
      if (!/^\d{6}$/.test(value)) return "Enter 6 digits only";
      return true;
    },
  });

  let validPermissionClassName = isValidRole("MASTER_CONFIG_THIRD_PARTY_HOTEL_MODIFY")
    ? ""
    : "btn-disable-permission";

  const resetOtpState = () => {
    setOtpSent(false);
    setOtpSuccessMessage('');
    setOtpSendLoading(false);
    setResendOtpSecondsLeft(0);
    clearErrors("otpSendError");
    if (resendOtpTimerRef.current) {
      clearInterval(resendOtpTimerRef.current);
      resendOtpTimerRef.current = null;
    }
  };

  const resetEditState = () => {
    reset({
      brandName: '',
      active: true,
      displayName: '',
      otp: '',
    });
    originalBrandRef.current = { brandName: '', displayName: '' };
    setEditBrandId(null);
    setIsViewMode(false);
    setIsEditMode(false);
    resetOtpState();
  };

  const handleOtpInputChange = (e) => {
    e.target.value = e.target.value.replace(/\D/g, "").slice(0, 6);
    otpFormOnChange(e);
  };

  const handleOtpKeyDown = (e) => {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (e.key.length === 1 && /\D/.test(e.key)) {
      e.preventDefault();
    }
  };

  const handleSendOtp = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    const newBrandName = String(brandNameWatched ?? '').trim();
    if (!newBrandName) {
      setError("otpSendError", { type: "manual", message: "Please enter the new brand name first." });
      return;
    }
    if (!editBrandId) return;

    clearErrors("otpSendError");
    setOtpSendLoading(true);
    callAPI(
      `/hotel-brand/${editBrandId}/generate-otp?brandName=${encodeURIComponent(newBrandName)}`,
      "POST",
      {}
    )
      .then((response) => {
        setOtpSent(true);
        setOtpSuccessMessage(response?.message || "OTP sent successfully to Tata Play SuperAdmin.");
        clearErrors("otpSendError");
        setResendOtpSecondsLeft(60);
        if (resendOtpTimerRef.current) clearInterval(resendOtpTimerRef.current);
        resendOtpTimerRef.current = setInterval(() => {
          setResendOtpSecondsLeft((prev) => {
            if (prev <= 1) {
              if (resendOtpTimerRef.current) {
                clearInterval(resendOtpTimerRef.current);
                resendOtpTimerRef.current = null;
              }
              setOtpSent(false);
              setOtpSuccessMessage('');
              clearErrors("otpSendError");
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      })
      .catch((error) => {
        setError("otpSendError", {
          type: "manual",
          message: error.response?.data?.errorMessage || "Failed to send OTP.",
        });
      })
      .finally(() => {
        setOtpSendLoading(false);
      });
  };

  const onSubmit = (data) => {
    if (isViewMode) return;

    const otpValue = String(data.otp ?? "").trim();
    const brandNameTrimmed = String(data.brandName ?? "").trim();
    const displayNameTrimmed = String(data.displayName ?? "").trim();
    const isBrandNameChanged = isBrandNameChangedFromOriginal(brandNameTrimmed);

    if (isBrandNameChanged && (!otpValue || !/^\d{6}$/.test(otpValue))) {
      setError("otp", {
        type: "manual",
        message: "OTP is required. Send OTP and enter the 6-digit code.",
      });
      return;
    }

    setLoader(true);

    if (isEditMode && editBrandId) {
      const payload = {
        brandName: brandNameTrimmed,
        displayName: displayNameTrimmed,
      };
      if (isBrandNameChanged) {
        payload.otp = otpValue;
      }

      callAPI(`/hotel-brand/${editBrandId}`, "PUT", payload)
        .then(function () {
          setLoader(false);
          resetEditState();
          setCreateThirdPartySuccessMsg("Successfully updated!");
          ThirdPartyListApi();
        })
        .catch(function (error) {
          setLoader(false);
          console.warn(error.response?.data?.errorMessage);
        });
      return;
    }

    const payload = {
      brandName: brandNameTrimmed,
      displayName: displayNameTrimmed,
      active: true,
    };

    callAPI(`/hotel-brand`, "POST", payload)
      .then(function (response) {
        setLoader(false);
        resetEditState();
        setCreateThirdPartySuccessMsg(response?.message || "Successfully saved!");
        ThirdPartyListApi();
      })
      .catch(function (error) {
        setLoader(false);
        console.warn(error.response?.data?.errorMessage);
      });
  };

  const isThirdPartyDeletedRef = useRef({
    isThirdPartyDeletedModalData: null,
    userConformationModalData: null,
  });

  const deleteThirdPartyConfigPopUpHandler = (row) => {
    setDeleteThirdPartyPopUp(true);
    const newModalRefData = {
      isThirdPartyDeletedModalData: {
        row
      },
      userConformationModalData: null,
    };
    isThirdPartyDeletedRef.current = newModalRefData;
  }

  const deleteThirdPartyHandler = () => {
    setConfirmValidate(true);
    if (confirmDeleteThirdPartyRow === "") {
      return
    }

    if (confirmDeleteThirdPartyRow === `Delete ${isThirdPartyDeletedRef.current?.isThirdPartyDeletedModalData.row.brandName}`) {
      setConfirmValidate(false)
      const { row } = isThirdPartyDeletedRef.current?.isThirdPartyDeletedModalData;
      callAPI(`/hotel-brand/${row?.id}`, "DELETE")
        .then((response) => {
          let successMessage = response.message
          const newModalRefData = {
            isThirdPartyDeletedModalData: null,
            userConformationModalData: {
              successMessage
            },
          };
          isThirdPartyDeletedRef.current = newModalRefData;
          ThirdPartyListApi();
        })
        .catch((error) => {
          console.warn(error)
        });
    }
  }

  const openThirdPartyBrand = (row) => {
    setLoader(true);
    callAPI(`/hotel-brand/${row?.id}`, "GET")
      .then(function (response) {
        const payload = {
          brandName: response?.brandName,
          active: response?.active,
          displayName: response?.displayName,
          otp: '',
        };
        originalBrandRef.current = {
          brandName: response?.brandName ?? '',
          displayName: response?.displayName ?? '',
        };
        reset(payload);
        setEditBrandId(row?.id);
        setIsEditMode(isSuperAdmin);
        setIsViewMode(!isSuperAdmin);
        resetOtpState();
        setLoader(false);
        ScrollToTop();
      })
      .catch(function (error) {
        setLoader(false);
        console.warn(error.response?.data?.errorMessage);
      });
  }

  const exitEditViewMode = () => {
    resetEditState();
  };

  const clearFormHandler = () => {
    resetEditState();
  };

  const renderActionIcon = (row) => {
    return (
      isValidRole("MASTER_CONFIG_THIRD_PARTY_HOTEL_MODIFY") ? <>
        <Tooltip title={isSuperAdmin ? "Edit" : "View"} arrow>
          <MdModeEditOutline
            className="edit-icon-enable cursor-pointer"
            onClick={() => openThirdPartyBrand(row)}
          />
        </Tooltip>
        <Tooltip title="Delete" className="cursor-pointer" arrow>
          <DeleteIcon
            className="delete-icon-enable"
            onClick={() => {
              deleteThirdPartyConfigPopUpHandler(row);
            }}
          />
        </Tooltip>
      </> : <>
        <Tooltip title="Don't have permission to view">
          <span>
            <MdModeEditOutline className="edit-icon-disable" />
          </span>
        </Tooltip>
        <Tooltip title="Don't have permission to delete">
          <DeleteIcon className="delete-icon-disable" arrow />
        </Tooltip>
      </>
    );
  };

  const columns = [
    { field: "createdBy", headerName: "Created By", flex: 1 },
    { field: "brandName", headerName: "Brand Name", flex: 1 },
    { field: "displayName", headerName: "Display Name", flex: 1 },
    {
      field: "actionCell",
      row: true,
      headerName: "Actions",
      flex: 0.5,
      renderCell: renderActionIcon,
      sortDirection: false,
      disableSortIcon: true,
    },
  ];

  const onPageChange = (event, value) => {
    setInputPageValue(value)
    setPaginationError("")
    setSelectedPage(value);
  };

  const ThirdPartyListApi = useCallback(() => {
    callAPI(`/hotel-brand`, "GET")
      .then((response) => {
        const data = Array.isArray(response) ? response : response?.brandResponse || [];
        if (data.length > 0) {
          setThirdPartyConfigList(data);
        }
        setTotalPageCount(response?.totalPages ? response?.totalPages : 1);
        setPaginationError("");
      })
      .catch((err) => console.warn(err.message));
  }, [selectedPage, recordsPerPage]);

  useEffect(() => {
    ThirdPartyListApi();
    setTotalPageCount(Math.ceil(thirdPartyConfigList.length / recordsPerPage));
  }, [ThirdPartyListApi, thirdPartyConfigList.length]);


  const popupCloseHandler = () => {
    setDeleteThirdPartyPopUp(false);
  };

  const formTitle = isEditMode
    ? 'Edit Third Party Hotel Configuration'
    : isViewMode
      ? 'View Third Party Hotel Configuration'
      : 'Third Party Hotel Configuration';

  return (
    <div className='row config-hotel config-management-form third-party-brand-form'>
      {loader && <div className="circular-progress"><CircularProgress /></div>}

      <div className='col-12 left-column'>
        <form
          autoComplete="off"
          onSubmit={handleSubmit(onSubmit)}
          className="hotel-list-form"
        >
          <div className="left-column-header d-flex align-items-center justify-content-between flex-wrap gap-2">
            <p className="Roboto-Bold mb-0">{formTitle}</p>
            {/* {(isViewMode || isEditMode) && (
              <button type="button" className="clear-btn" onClick={exitEditViewMode}>
                Close
              </button>
            )} */}
          </div>

          <div className="d-flex gap-3 flex-wrap">
            <div className="form-group-hotel third-party-field-half">
              <div className="floating-label">
                <FaHotel className="new-hotel-icons" />
                <input
                  id="brandName"
                  maxLength={50}
                  placeholder=""
                  disabled={isViewMode}
                  readOnly={isViewMode}
                  {...register("brandName", {
                    required: isViewMode ? false : "Brand Name is mandatory",
                  })}
                />
                <label htmlFor="brandName">Brand Name</label>
              </div>
              {errors.brandName && (
                <small className="required">{errors.brandName.message}</small>
              )}
            </div>

            <div className="form-group-hotel third-party-field-half">
              <div className="floating-label">
                <FontAwesomeIcon icon={faLink} className="new-hotel-icons" />
                <input
                  id="displayName"
                  maxLength={25}
                  placeholder=""
                  disabled={isViewMode}
                  readOnly={isViewMode}
                  {...register("displayName", {
                    required: isViewMode ? false : "Display Name is required",
                    maxLength: {
                      value: 25,
                      message: "Maximum length is 25 characters",
                    },
                  })}
                />
                <label htmlFor="displayName">Display Name</label>
              </div>
              {errors.displayName && (
                <small className="required">{errors.displayName.message}</small>
              )}
            </div>
          </div>

          {!isViewMode && (
            <>
              <div className="d-flex gap-3 flex-wrap third-party-action-row mt-3 w-100">
                {brandNameChanged && (
                  <div className="form-group-hotel third-party-field-half third-party-otp-col">
                    <div className="floating-label third-party-otp-floating">
                      <div className="third-party-otp-control">
                        <input
                          id="otp"
                          className="otp-input-inner"
                          placeholder=""
                          type="text"
                          inputMode="numeric"
                          maxLength={6}
                          autoComplete="one-time-code"
                          {...otpFieldRegister}
                          onChange={handleOtpInputChange}
                          onKeyDown={handleOtpKeyDown}
                        />
                        <button
                          type="button"
                          className="send-otp-inside"
                          onClick={handleSendOtp}
                          disabled={otpSendLoading || resendOtpSecondsLeft > 0}
                        >
                          {otpSendLoading ? "Sending..." : resendOtpSecondsLeft > 0 ? `Resend (${resendOtpSecondsLeft}s)` : "Send OTP"}
                        </button>
                      </div>
                      <label htmlFor="otp">Enter OTP here<span className="required">*</span></label>
                    </div>
                  </div>
                )}
                <div className="d-flex gap-2 ms-auto third-party-action-buttons">
                  <button type="button" className="clear-btn" onClick={clearFormHandler}>
                    Clear All
                  </button>
                  <button type="submit" className={`create-btn ${validPermissionClassName}`}>
                    {isEditMode ? 'Update' : 'Create'}
                  </button>
                </div>
              </div>
              {brandNameChanged && (
                <div className="form-group-hotel third-party-otp-messages">
                  {errors.otp && (
                    <p className="required mb-0">{errors.otp.message}</p>
                  )}
                  {errors.otpSendError && (
                    <p className="required mb-0">{errors.otpSendError.message}</p>
                  )}
                  {otpSent && otpSuccessMessage && (
                    <p className="mb-0 text-success">{otpSuccessMessage}</p>
                  )}
                  {/* {resendOtpSecondsLeft > 0 && (
                    <p className="mb-0 text-muted small">Resend OTP after {resendOtpSecondsLeft} seconds.</p>
                  )} */}
                </div>
              )}
            </>
          )}
        </form>

        <div className="hotel-list">
          <p><b>Third Party Hotel Brand List</b></p>
          <TableWrapper
            data={thirdPartyConfigList.slice((selectedPage - 1) * recordsPerPage, selectedPage * recordsPerPage)}
            columns={columns}
            noPagination={true}
            module="third-party-hotel-config"
            expandable={true}
          />
          <Custom_Paingation
            selectedPage={selectedPage}
            totalPageCount={totalPageCount}
            paginationError={paginationError}
            inputPageValue={inputPageValue}
            setSelectedPage={setSelectedPage}
            setPaginationError={setPaginationError}
            setInputPageValue={setInputPageValue}
            onPageChange={onPageChange}
          />
        </div>
      </div>

      {deleteThirdPartyPopUp && <DeleteThirdPartyConfig
        deleteThirdPartyPopUp={deleteThirdPartyPopUp}
        isThirdPartyDeletedRef={isThirdPartyDeletedRef}
        popupCloseHandler={popupCloseHandler}
        deleteThirdPartyHandler={deleteThirdPartyHandler}
        setConfirmDeleteThirdPartyRow={setConfirmDeleteThirdPartyRow}
        confirmDeleteThirdPartyRow={confirmDeleteThirdPartyRow}
        confirmValidate={confirmValidate}
      />}

      <BasicModal
        popupOpen={createThirdPartySuccessMsg !== ""}
        logo={"/Images/approve-qc.png"}
        bodyMessage={<p className="mt-2">{createThirdPartySuccessMsg}</p>}
        footer={{
          secondaryButton: {
            buttonName: "OK",
            action: () => {
              setCreateThirdPartySuccessMsg('');
              ThirdPartyListApi();
            },
            buttonPrimaryCustomCss: 'cancel-btn_new'
          }
        }}
      />
    </div>
  )
}

export default ThirdPartyHotelConfiguration;
